import { AxiosResponse } from 'axios'
import { queryOptions } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { NAdmin } from '../interfaces/services/admin.interfaces'
import { createCsvBlob } from '../helpers/csv'
import { createUrl } from './helpers'
import axiosAuth from './config'

export const hasPermissions = (): Promise<
  AxiosResponse<NAdmin.NHasPermissions.IResponse>
> => {
  return axiosAuth.get<NAdmin.NHasPermissions.IResponse>(
    createUrl('/admin/has-permissions')
  )
}

export const getDiscountCodes = (): Promise<
  AxiosResponse<NAdmin.NGetDiscount.IResponse>
> => {
  return axiosAuth.get<NAdmin.NGetDiscount.IResponse>(
    createUrl('/admin/discounts')
  )
}

export const createDiscount = (
  data: NAdmin.NCreateDiscount.IRequestBody
): Promise<AxiosResponse<NAdmin.NCreateDiscount.IResponse>> => {
  return axiosAuth.post<NAdmin.NCreateDiscount.IResponse>(
    createUrl('/admin/discounts'),
    data
  )
}

export const patchDiscountCode = ({
  code,
  body,
}: {
  code: string
  body: NAdmin.NPatchDiscount.IRequestBody
}): Promise<AxiosResponse<NAdmin.NPatchDiscount.IResponse>> => {
  return axiosAuth.patch<NAdmin.NPatchDiscount.IResponse>(
    createUrl(`/admin/discounts/${code}`),
    body
  )
}

export const removeDiscountCode = ({
  code,
}: {
  code: string
}): Promise<AxiosResponse<NAdmin.NRemoveDiscount.IResponse>> => {
  return axiosAuth.delete<NAdmin.NRemoveDiscount.IResponse>(
    createUrl(`/admin/discounts/${code}`)
  )
}

export const searchUser = (
  data: NAdmin.NSearchUser.IRequestBody
): Promise<AxiosResponse<NAdmin.NSearchUser.IResponse>> => {
  return axiosAuth.post<NAdmin.NSearchUser.IResponse>(
    createUrl(`/admin/search-user`),
    data
  )
}

export const searchSociety = (
  data: NAdmin.NSearchSociety.IRequestBody
): Promise<AxiosResponse<NAdmin.NSearchSociety.IResponse>> => {
  return axiosAuth.post<NAdmin.NSearchSociety.IResponse>(
    createUrl(`/admin/search-society`),
    data
  )
}

export const searchSocietyAuditLogs = (
  id: string,
  queryParameters: NAdmin.NSearchAuditLogs.IRequestQuery
): Promise<AxiosResponse<NAdmin.NSearchAuditLogs.IResponse>> => {
  return axiosAuth.get<NAdmin.NSearchAuditLogs.IResponse>(
    createUrl(`/admin/search-society-audit-logs/${id}`, { ...queryParameters })
  )
}

export const getPendingInvoices = (
  date?: string
): Promise<AxiosResponse<NAdmin.NGetPendingInvoices.IResponse>> => {
  return axiosAuth.get<NAdmin.NGetPendingInvoices.IResponse>(
    createUrl('/admin/pending-invoices', { date })
  )
}

export const loginAsUser = (
  data: NAdmin.NLoginAsUser.IRequestBody
): Promise<AxiosResponse<NAdmin.NLoginAsUser.IResponse>> => {
  return axiosAuth.post<NAdmin.NLoginAsUser.IResponse>(
    createUrl('/admin/login-as-user'),
    data
  )
}

export const changeUserPassword = (
  data: NAdmin.NChangeUserPassword.IRequestBody
): Promise<AxiosResponse<NAdmin.NChangeUserPassword.IResponse>> => {
  return axiosAuth.post<NAdmin.NChangeUserPassword.IResponse>(
    createUrl('/admin/change-user-password'),
    data
  )
}

export const changeUserEmail = (
  data: NAdmin.NChangeUserEmail.IRequestBody
): Promise<AxiosResponse<NAdmin.NChangeUserEmail.IResponse>> => {
  return axiosAuth.post<NAdmin.NChangeUserEmail.IResponse>(
    createUrl('/admin/change-user-email'),
    data
  )
}

export const inactivateSociety = (
  data: NAdmin.NInactivateSociety.IRequestBody
): Promise<AxiosResponse<NAdmin.NInactivateSociety.IResponse>> => {
  return axiosAuth.post<NAdmin.NInactivateSociety.IResponse>(
    createUrl('/admin/inactivate-society'),
    data
  )
}

export const patchSociety = (
  body: NAdmin.NPatchSociety.IRequestBody
): Promise<AxiosResponse<NAdmin.NPatchSociety.IResponse>> => {
  return axiosAuth.post<NAdmin.NPatchSociety.IResponse>(
    createUrl(`/admin/patch-society`),
    body
  )
}

export const patchSocietyWebsite = (
  body: NAdmin.NPatchSocietyWebsite.IRequestBody
): Promise<AxiosResponse<NAdmin.NPatchSocietyWebsite.IResponse>> => {
  return axiosAuth.post<NAdmin.NPatchSocietyWebsite.IResponse>(
    createUrl(`/admin/patch-society-website`),
    body
  )
}

export const deleteSociety = (
  body: NAdmin.NDeleteSociety.IRequestBody
): Promise<AxiosResponse<NAdmin.NDeleteSociety.IResponse>> => {
  return axiosAuth.post<NAdmin.NDeleteSociety.IResponse>(
    createUrl(`/admin/delete-society`),
    body
  )
}

export const deleteUser = (
  body: NAdmin.NDeleteUser.IRequestBody
): Promise<AxiosResponse<NAdmin.NDeleteUser.IResponse>> => {
  return axiosAuth.post<NAdmin.NDeleteUser.IResponse>(
    createUrl(`/admin/delete-user`),
    body
  )
}

export const getSocietiesByIds = (
  body: NAdmin.NGetSocietiesByIds.IRequestBody
): Promise<AxiosResponse<NAdmin.NGetSocietiesByIds.IResponse>> => {
  return axiosAuth.post<NAdmin.NGetSocietiesByIds.IResponse>(
    createUrl(`/admin/get-societies-by-ids`),
    body
  )
}

export const upgradeSociety = (
  body: NAdmin.NUpgradeSociety.IRequestBody
): Promise<AxiosResponse<NAdmin.NUpgradeSociety.IResponse>> => {
  return axiosAuth.post<NAdmin.NUpgradeSociety.IResponse>(
    createUrl(`/admin/upgrade-society`),
    body
  )
}

export const downgradeSociety = (
  body: NAdmin.NDowngradeSociety.IRequestBody
): Promise<AxiosResponse<NAdmin.NDowngradeSociety.IResponse>> => {
  return axiosAuth.post<NAdmin.NDowngradeSociety.IResponse>(
    createUrl(`/admin/downgrade-society`),
    body
  )
}

const convertJsonBlobToCsvBlob = async (jsonBlob: Blob): Promise<Blob> => {
  const parsedResponse = JSON.parse(await jsonBlob.text())
  const json: Record<string, unknown>[] = parsedResponse.data
  const header = Object.keys(json[0] ?? {})
  const rows = json.map((row) => header.map((field) => row[field]))
  return createCsvBlob(rows, header)
}

export const downloadSocietyList = async (): Promise<void> => {
  await axiosAuth({
    url: createUrl('/admin/download-societies'),
    method: 'POST',
    responseType: 'blob',
  }).then(async (resp) => {
    const csvBlob = await convertJsonBlobToCsvBlob(resp.data)
    const href = URL.createObjectURL(csvBlob)

    const link = document.createElement('a')
    link.href = href
    link.setAttribute(
      'download',
      `societies_statistic_${dayjs().format('YYYYMMDD')}.csv`
    )
    document.body.appendChild(link)
    link.click()

    document.body.removeChild(link)
    URL.revokeObjectURL(href)
  })
}

export const downloadAdminBoardMembersEmailList = async (): Promise<void> => {
  const response = await axiosAuth.get<Blob>(
    createUrl('/admin/download-admin-board-members-csv'),
    { responseType: 'blob' }
  )

  const filename = `societies_admins_board_${dayjs().format('YYYYMMDD')}.csv`

  const href = URL.createObjectURL(response.data)
  const link = document.createElement('a')
  link.href = href
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()

  link.remove()
  URL.revokeObjectURL(href)
}

export const listWebinars = async (): Promise<
  AxiosResponse<NAdmin.NGetWebinar.IResponse>
> => {
  return axiosAuth.get<NAdmin.NGetWebinar.IResponse>(
    createUrl('/admin/webinars')
  )
}

export const createWebinar = async (
  body: NAdmin.NCreateWebinar.IRequestBody
): Promise<AxiosResponse<NAdmin.NCreateWebinar.IResponse>> => {
  return axiosAuth.post<NAdmin.NCreateWebinar.IResponse>(
    createUrl('/admin/webinars'),
    body
  )
}

export const patchWebinar = async ({
  id,
  body,
}: {
  body: NAdmin.NPatchWebinar.IRequestBody
  id: string
}): Promise<AxiosResponse<NAdmin.NPatchWebinar.IResponse>> => {
  return axiosAuth.patch<NAdmin.NPatchWebinar.IResponse>(
    createUrl(`/admin/webinars/${id}`),
    body
  )
}

export const removeWebinar = async ({
  id,
}: {
  id: string
}): Promise<AxiosResponse<NAdmin.NRemoveWebinar.IResponse>> => {
  return axiosAuth.delete<NAdmin.NRemoveWebinar.IResponse>(
    createUrl(`/admin/webinars/${id}`)
  )
}

export const getWebinar = async (
  id?: string
): Promise<AxiosResponse<NAdmin.NGetWebinar.IResponse>> => {
  return axiosAuth.get<NAdmin.NGetWebinar.IResponse>(
    createUrl(`/admin/webinars/${id}`)
  )
}

export const adminQueries = {
  hasPermissions: (userId?: string) =>
    queryOptions({
      queryKey: [{ scope: 'admin', entity: 'hasPermissions', userId }] as const,
      queryFn: () => hasPermissions(),
      enabled: !!userId,
    }),
  getDiscountCodes: () =>
    queryOptions({
      queryKey: [{ scope: 'admin', entity: 'getDiscountCodes' }] as const,
      queryFn: async () => {
        const response = await getDiscountCodes()
        return response.data.data
      },
    }),
  webinars: () =>
    queryOptions({
      queryKey: [{ scope: 'admin', entity: 'webinars' }] as const,
      queryFn: () => listWebinars(),
    }),

  webinar: (webinar?: string) =>
    queryOptions({
      queryKey: [{ ...adminQueries.webinars().queryKey[0], webinar }] as const,
      queryFn: ({ queryKey }) => getWebinar(queryKey[0]?.webinar),
    }),
  getSocietiesByIds: (societyId?: string) =>
    queryOptions({
      queryKey: [{ scope: 'admin', entity: 'society', societyId }] as const,
      queryFn: async ({ queryKey }) => {
        return (
          await getSocietiesByIds({ societyIds: [queryKey[0].societyId!] })
        ).data.data
      },
      enabled: !!societyId,
    }),
  searchUser: (query?: string) =>
    queryOptions({
      queryKey: [{ scope: 'admin', entity: 'searchUser', query }] as const,
      enabled: !!query,
      queryFn: async ({ queryKey }) => {
        return (await searchUser({ query: queryKey[0].query })).data.data
      },
    }),
  searchSociety: (query?: string) =>
    queryOptions({
      queryKey: [{ scope: 'admin', entity: 'societySearch', query }] as const,
      enabled: !!query,
      queryFn: async ({ queryKey }) => {
        return (await searchSociety({ query: queryKey[0].query })).data.data
      },
    }),
}
